import React, { useState } from "react";
import LendyIcon from "../../components/lendy/LendyIcon";
import AskAddyV2 from "../../models/askAddyV2";
import {
  JOB_GROUPS,
  formatNextRun,
  getJobIcon,
  getJobScope,
  getJobSummary,
} from "./autopilotFormat";
import NewJobForm from "./NewJobForm";
import useAsyncAction from "./useAsyncAction";

/** One task: a switch to pause it, and its details, Run now and Delete when opened. */
const JobRow = ({ job, identity, loanId, onChanged }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const { isBusy, notice, run } = useAsyncAction(onChanged);
  const { id, name, prompt, kind, enabled, nextRunAt, timezone } = job;
  const nextRun = enabled && kind !== "event" ? formatNextRun(nextRunAt, timezone) : "";
  const when = [getJobScope(job, loanId), nextRun && `Next ${nextRun}`].filter(Boolean).join(" · ");

  return (
    <li className="lendy-profile-entry">
      <div className="lendy-profile-item">
        <button
          type="button"
          className="lendy-profile-item-main"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((wasOpen) => !wasOpen)}
        >
          <span className="lendy-profile-tile">
            <LendyIcon name={getJobIcon(job)} />
          </span>
          <span className="lendy-profile-item-text">
            <b>{name}</b>
            <small>{getJobSummary(job)}</small>
          </span>
        </button>
        <button
          type="button"
          className="lendy-switch"
          role="switch"
          aria-checked={enabled}
          aria-label={`${name}: ${enabled ? "on" : "paused"}`}
          disabled={isBusy}
          onClick={() => run(() => AskAddyV2.autopilot.updateJob(id, { enabled: !enabled }, identity))}
        />
      </div>
      {isOpen && (
        <div className="lendy-profile-detail">
          {prompt && <p>{prompt}</p>}
          <small>{when}</small>
          <div className="lendy-profile-replies">
            {isConfirmingDelete ? (
              <>
                <span>Delete this task?</span>
                <button
                  type="button"
                  className="lendy-pill-button lendy-pill-button--danger"
                  disabled={isBusy}
                  onClick={() => run(() => AskAddyV2.autopilot.deleteJob(id, identity))}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="lendy-btn lendy-btn--text"
                  onClick={() => setIsConfirmingDelete(false)}
                >
                  Keep
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="lendy-pill-button"
                  disabled={isBusy}
                  onClick={() =>
                    run(
                      () => AskAddyV2.autopilot.runJob(id, identity),
                      "Running. You'll get a notification with the report."
                    )
                  }
                >
                  Run now
                </button>
                <button
                  type="button"
                  className="lendy-btn lendy-btn--text"
                  onClick={() => setIsConfirmingDelete(true)}
                >
                  Delete
                </button>
              </>
            )}
          </div>
          {notice && <p className="lendy-profile-note">{notice}</p>}
        </div>
      )}
    </li>
  );
};

// Ready-made tasks for a first one: a click fills in the form, nothing is created until Create task.
const QUICK_STARTS = [
  { name: "Morning pipeline check", prompt: "Tell me which loans need my attention today and why.", timing: "weekdays", time: "08:00" },
  { name: "Expiring rate locks", prompt: "List the rate locks that expire in the next 7 days.", timing: "weekdays", time: "08:00" },
  { name: "End-of-day recap", prompt: "Summarize what changed on my loans today.", timing: "weekdays", time: "17:00" },
];

/** The scheduled tasks, grouped by how they run (every hour, at a time, when a loan changes). */
const ScheduleTab = ({ jobs, error, identity, loanId, assistantName, onChanged }) => {
  // null: no form; {}: a blank task; a quick start: the form filled in from it.
  const [draft, setDraft] = useState(null);
  const isCreating = draft !== null;

  if (!jobs) {
    return (
      <p className="lendy-profile-note">
        {error ? `Can't load your scheduled tasks right now: ${error}` : "Loading…"}
      </p>
    );
  }

  const groups = JOB_GROUPS.map((group) => ({
    ...group,
    jobs: jobs.filter(({ kind }) => kind === group.kind),
  })).filter((group) => group.jobs.length > 0);

  return (
    <>
      {groups.map(({ kind, label, jobs: groupJobs }) => (
        <section key={kind} className="lendy-profile-group" aria-label={label}>
          <h3 className="lendy-profile-heading">{label}</h3>
          <ul className="lendy-profile-list">
            {groupJobs.map((job) => (
              <JobRow key={job.id} job={job} identity={identity} loanId={loanId} onChanged={onChanged} />
            ))}
          </ul>
        </section>
      ))}
      {!jobs.length && !isCreating && (
        <div className="lendy-profile-empty">
          <span className="lendy-profile-empty-icon">
            <LendyIcon name="calendar" size={22} />
          </span>
          <b>No scheduled tasks yet</b>
          <p>Ask {assistantName} in the chat, or start with one of these.</p>
          <ul className="lendy-profile-starts">
            {QUICK_STARTS.map((start) => (
              <li key={start.name}>
                <button type="button" onClick={() => setDraft(start)}>
                  <LendyIcon name="clock" size={16} />
                  <span>
                    <b>{start.name}</b>
                    <small>{start.prompt}</small>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <button type="button" className="lendy-pill-button lendy-profile-empty-action" onClick={() => setDraft({})}>
            <LendyIcon name="plus" size={16} />
            New task
          </button>
        </div>
      )}
      {isCreating ? (
        <NewJobForm
          identity={identity}
          loanId={loanId}
          assistantName={assistantName}
          initial={draft}
          onCreated={() => {
            setDraft(null);
            onChanged();
          }}
          onCancel={() => setDraft(null)}
        />
      ) : (
        jobs.length > 0 && (
          <button type="button" className="lendy-pill-button lendy-profile-add" onClick={() => setDraft({})}>
            <LendyIcon name="plus" size={14} />
            New task
          </button>
        )
      )}
    </>
  );
};

export default ScheduleTab;
